import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import LeadForm from "../components/LeadForm";
import AppShowcase from "../components/AppShowcase";
import { ArrowRight, Wallet, CalendarDays, Smartphone, ShieldCheck, BadgeCheck, Sparkles, Check } from "lucide-react";
import { trackCtaClick } from "../lib/analytics";

const ICONS = [Wallet, CalendarDays, Smartphone, BadgeCheck, ShieldCheck, Sparkles];

export default function Huseier() {
  const { t, lang } = useLang();
  const [price, setPrice] = useState(80);
  const [days, setDays] = useState(20);

  useSeo({ title: t.seo.huseier.title, description: t.seo.huseier.description, path: "/huseier", lang });

  const monthly = useMemo(() => Math.round(price * days), [price, days]);

  return (
    <main data-testid="huseier-page" className="bg-ink text-cream pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="huseier-hero">
        <div className="absolute inset-0 bay-lines opacity-40 pointer-events-none" />
        <div
          className="absolute -top-20 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,107,0,0.16) 0%, rgba(255,107,0,0) 70%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-16 md:pt-20 md:pb-24 relative grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <motion.span
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-amber"
            >
              <span className="w-1.5 h-1.5 bg-amber rounded-full" />
              {t.huseier.heroEyebrow}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-7 font-display text-4xl sm:text-6xl md:text-7xl font-extrabold leading-[1.02] tracking-tighter"
              style={{ letterSpacing: "-0.04em" }}
              data-testid="huseier-headline"
            >
              {t.huseier.heroTitle}<br />
              <span className="text-amber">{t.huseier.heroTitleAccent}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-7 font-display text-xl md:text-2xl font-bold text-amber max-w-xl leading-snug tracking-tight"
              style={{ letterSpacing: "-0.02em" }}
            >
              {t.huseier.heroSub}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-4 text-base md:text-lg text-cream/70 max-w-xl leading-relaxed"
            >
              {t.huseier.heroLead}
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.28 }}
              className="mt-7 space-y-3"
              data-testid="huseier-hero-bullets"
            >
              {t.huseier.heroBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="text-amber mt-1 shrink-0" strokeWidth={3} />
                  <span className="text-sm md:text-base text-cream/80 leading-relaxed">
                    <span className="font-bold text-cream">{b.title}:</span> {b.body}
                  </span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.32 }}
              className="mt-8 border-l-2 border-amber pl-4"
              data-testid="huseier-hero-stations"
            >
              <p className="text-sm md:text-base font-bold text-cream">{t.huseier.stationsLead}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {t.huseier.stations.map((s) => (
                  <span
                    key={s}
                    data-testid={`huseier-station-${s.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 border border-amber/40 bg-amber/10 px-3 py-1 text-xs md:text-sm font-bold tracking-wide text-amber"
                  >
                    <span className="w-1.5 h-1.5 bg-amber rounded-full" />
                    {s}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.36 }}
              className="mt-9"
            >
              <a
                href="#huseier-form"
                data-testid="huseier-hero-cta"
                onClick={() => trackCtaClick("huseier", "hero")}
                className="inline-flex items-center gap-3 h-14 px-6 bg-amber text-ink font-display font-bold text-base md:text-lg hover:bg-amber-hover transition-colors"
              >
                {t.huseier.heroCta} <ArrowRight size={20} />
              </a>
            </motion.div>
          </div>

          {/* App showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-5 flex justify-center md:justify-end md:pt-4"
          >
            <AppShowcase />
          </motion.div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="bg-cream text-ink py-20 md:py-28" data-testid="huseier-calculator">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <span className="text-xs font-bold tracking-widest uppercase text-orange">Kalkulator</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.huseier.calcTitle}
            </h2>
            <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
              {t.huseier.calcNote}
            </p>
          </div>

          <div className="md:col-span-7 bg-ink text-cream p-7 md:p-10 relative l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br border border-amber/30">
            <div className="space-y-8">
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-xs font-bold tracking-widest uppercase text-amber/80">{t.huseier.calcDailyLabel}</label>
                  <span className="font-display text-2xl font-bold text-amber">{price} kr</span>
                </div>
                <input
                  type="range" min="30" max="200" step="5" value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  data-testid="calc-price-slider"
                  className="w-full accent-amber"
                />
                <div className="flex justify-between text-xs text-cream/40 mt-2">
                  <span>30 kr</span><span>200 kr</span>
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-xs font-bold tracking-widest uppercase text-amber/80">{t.huseier.calcDaysLabel}</label>
                  <span className="font-display text-2xl font-bold text-amber">{days} dager</span>
                </div>
                <input
                  type="range" min="0" max="22" step="1" value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="calc-days-slider"
                  className="w-full accent-amber"
                />
                <div className="flex justify-between text-xs text-cream/40 mt-2">
                  <span>0</span><span>22</span>
                </div>
              </div>

              <div className="pt-6 border-t border-amber/20">
                <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-2">
                  {t.huseier.calcResultLabel}
                </p>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-5xl md:text-6xl font-extrabold text-amber" style={{ letterSpacing: "-0.04em" }} data-testid="calc-result-monthly">
                    {monthly.toLocaleString("nb-NO")}
                  </span>
                  <span className="font-display text-xl text-cream/60">{t.huseier.calcMonthly}</span>
                </div>
                <p className="mt-2 text-sm text-cream/50">
                  Brutto potensial. Pendli tar en mindre tjenestavgift — endelig prisstruktur bekreftes før lansering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-ink py-20 md:py-28" data-testid="huseier-values">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-20 max-w-3xl" style={{ letterSpacing: "-0.04em" }}>
            {t.huseier.valuesTitle}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {t.huseier.values.map((v, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  className="relative bg-amber-deep border border-amber/20 p-7 md:p-8 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
                  data-testid={`huseier-value-${i+1}`}
                >
                  <Icon size={28} className="text-amber mb-5" strokeWidth={1.5} />
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mb-2">{v.title}</h3>
                  <p className="text-cream/70 text-sm md:text-base leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="huseier-form" className="bg-cream text-ink py-20 md:py-28" data-testid="huseier-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <span className="text-xs font-bold tracking-widest uppercase text-orange">{t.huseier.formEyebrow}</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.huseier.formTitle}
            </h2>
            <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
              {t.huseier.formSub}
            </p>

            <div className="mt-10 space-y-4">
              {["Du setter prisen", "Pendli håndterer hele betalingen", "Founder-prioritet"].map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-orange rounded-full" />
                  <span className="text-sm md:text-base font-medium">{p}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="bg-ink p-1">
              <LeadForm
                role="huseier"
                locationLabel="address"
                source="huseier-page"
                testIdPrefix="huseier-lead"
                submitLabel={t.huseier.heroCta}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ink py-20 md:py-28 border-t border-amber/10" data-testid="huseier-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-16" style={{ letterSpacing: "-0.04em" }}>
            {t.huseier.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.huseier.faqs.map((f, i) => (
              <details key={i} className="group border-t border-amber/20 py-5 cursor-pointer" data-testid={`huseier-faq-${i+1}`}>
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight group-hover:text-amber transition-colors">
                    {f.q}
                  </h3>
                  <span className="text-amber text-2xl font-bold leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-cream/70 text-base md:text-lg leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
